/* DealRoomSheets.jsx — Operational slide-over sheets.
 *
 * The Deal Room composer chips (Upload · Email · Log call · Task) and
 * the Message All CTA each open a calm right-side slide-over sheet on
 * desktop and a bottom sheet on mobile. Doctrine: instant, premium,
 * operational. No heavy modals.
 *
 * Each sheet:
 *   - posts to the appropriate real backend endpoint;
 *   - emits a deal_activities row so the live feed updates instantly;
 *   - invalidates the activity cache so the centre column + intel rail
 *     + Today page all re-render with the new state.
 *
 * Surfaces exported:
 *   - SlideOver           — generic right-side sheet shell
 *   - UploadSheet         — multipart upload + drag/drop + auto-category
 *   - EmailSheet          — recipient pick + subject + body
 *   - CallLogSheet        — participant + summary + outcome + follow-up
 *   - TaskSheet           — title + due date + assignee + priority
 *   - MessageAllSheet     — broadcast composer (pre-selects everyone)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  AlertCircle, ArrowRight, Check, CheckCircle2, ChevronDown, FileText,
  Loader2, Mail, Paperclip, Phone, PoundSterling, Send, ShieldCheck,
  Sparkles, Upload as UploadIcon, UserPlus, Users, X, ListChecks, Clock,
} from "lucide-react";
import { api } from "../../lib/api";
import { invalidateDealActivity } from "../../hooks/useDealActivity";
import { sendOperationalEmail, emailableRecipients } from "../../lib/emailClient";
import { useChainStore } from "../../data/chainStore";
import { hasActiveRequestForTemplate } from "../../lib/documentRequestLaunch";

/* ───────────────────────────────────────────────────────────────
   Generic slide-over shell
   ─────────────────────────────────────────────────────────────── */
export function SlideOver({ open, onClose, title, subtitle, children, footer, wide = false, testid }) {
  const sheetRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    // iOS keyboard reveal — when a textarea / input gains focus inside
    // the sheet, the soft keyboard covers the bottom 40-50% of the
    // viewport. Calmly scroll the focused field into the middle of the
    // remaining visible area so the operator isn't typing blind.
    const sheetEl = sheetRef.current;
    let scrollTimer = null;
    const onFocusIn = (ev) => {
      const tag = String(ev.target?.tagName || "").toLowerCase();
      if (tag !== "textarea" && tag !== "input" && tag !== "select") return;
      if (typeof window === "undefined") return;
      // Defer so the keyboard has time to claim its space before we
      // measure — iOS Safari resizes visualViewport on keyboard reveal.
      if (scrollTimer) clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        try {
          ev.target.scrollIntoView({ block: "center", behavior: "smooth" });
        } catch { /* noop */ }
      }, 280);
    };
    if (sheetEl) sheetEl.addEventListener("focusin", onFocusIn);

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      if (sheetEl) sheetEl.removeEventListener("focusin", onFocusIn);
      if (scrollTimer) clearTimeout(scrollTimer);
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[80]" role="dialog" aria-modal="true" aria-label={title} data-testid={testid}>
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px]"
        style={{ animation: "cf-sheet-fade 200ms ease-out" }}
        data-testid={`${testid}-backdrop`}
      />
      {/* Sheet */}
      <aside
        ref={sheetRef}
        className={`absolute right-0 top-0 bottom-0 w-full ${wide ? "sm:max-w-[640px]" : "sm:max-w-[480px]"} bg-white shadow-2xl flex flex-col`}
        style={{ animation: "cf-sheet-in 260ms cubic-bezier(0.16,1,0.3,1)" }}
      >
        <header className="px-5 md:px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-[16px] md:text-[17px] font-bold tracking-[-0.01em] text-slate-900 leading-tight">{title}</h2>
            {subtitle && <p className="text-[12px] text-slate-500 mt-0.5 leading-snug">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-lg inline-flex items-center justify-center text-slate-400 hover:text-slate-900 hover:bg-slate-50 transition-colors"
            data-testid={`${testid}-close`}
          >
            <X size={14} strokeWidth={2.4} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 md:px-6 py-5">{children}</div>
        {footer && <div className="px-5 md:px-6 py-3.5 border-t border-slate-100 bg-white">{footer}</div>}
      </aside>
      <style>{`
        @keyframes cf-sheet-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes cf-sheet-in   { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @media (max-width: 640px) {
          aside { left: 0 !important; top: auto !important; height: 92dvh !important; border-top-left-radius: 24px; border-top-right-radius: 24px; }
          @keyframes cf-sheet-in { from { transform: translateY(100%); } to { transform: translateY(0); } }
        }
      `}</style>
    </div>,
    document.body,
  );
}

/* ─── Shared small atoms ─────────────────────────────────── */
function Label({ children, htmlFor }) {
  return (
    <label htmlFor={htmlFor} className="block text-[10.5px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-1.5">
      {children}
    </label>
  );
}
const inputCls =
  "w-full h-10 rounded-xl bg-white border border-slate-200 px-3 text-[13px] text-slate-900 placeholder:text-slate-400 outline-none focus:border-violet-300 focus:ring-2 focus:ring-violet-100 transition";
const textareaCls =
  "w-full rounded-xl bg-white border border-slate-200 px-3 py-2.5 text-[13px] text-slate-900 placeholder:text-slate-400 outline-none focus:border-violet-300 focus:ring-2 focus:ring-violet-100 transition resize-none leading-relaxed";

function PrimaryButton({ children, onClick, disabled, loading, testid, type = "button", icon: Icon = Send }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className="h-10 px-4 rounded-xl text-white text-[13px] font-semibold inline-flex items-center gap-1.5 transition-all hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        background: "linear-gradient(135deg,#8B5CF6,#6366F1)",
        boxShadow: "0 8px 22px -10px rgba(99,102,241,0.55)",
      }}
      data-testid={testid}
    >
      {loading ? <Loader2 size={13} className="animate-spin" /> : <Icon size={12} strokeWidth={2.4} />}
      {children}
    </button>
  );
}

function GhostButton({ children, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-10 px-3.5 rounded-xl text-[12.5px] font-semibold text-slate-600 hover:text-slate-900 transition-colors"
      data-testid={testid}
    >
      {children}
    </button>
  );
}

/* Calm inline error banner — preserves the agent's draft and offers
 * dismiss + retry behaviour. Used on every sheet that sends email. */
function InlineError({ message, onDismiss, testid }) {
  return (
    <div
      className="mt-4 rounded-xl border border-rose-200 bg-rose-50/60 px-3.5 py-2.5 flex items-start gap-2.5"
      role="alert"
      data-testid={testid}
    >
      <AlertCircle size={13} className="text-rose-500 mt-0.5 shrink-0" strokeWidth={2.3} />
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-semibold text-rose-900">Delivery didn't go through.</p>
        <p className="text-[11px] text-rose-700/90 mt-0.5 leading-snug">{message}</p>
        <p className="text-[11px] text-rose-600/90 mt-1">Your draft is preserved — tap Send to try again.</p>
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="w-6 h-6 rounded inline-flex items-center justify-center text-rose-500 hover:bg-rose-100 transition-colors shrink-0"
          aria-label="Dismiss"
          data-testid={`${testid}-dismiss`}
        >
          <X size={11} />
        </button>
      )}
    </div>
  );
}

/* ─── Folder auto-categorisation ─────────────────────────── */
const FOLDER_RULES = [
  { match: /aml|id|kyc|passport|proof.?of.?fund/i, folder: "AML" },
  { match: /memo|memorand/i,                       folder: "Memo" },
  { match: /ta6|ta10|contract|exchange/i,          folder: "Contracts" },
  { match: /mortgage|offer|valuation/i,            folder: "Mortgage" },
  { match: /search|drainage|environment|chancel/i, folder: "Searches" },
  { match: /lease|management.?pack|lpe|service.?charge/i, folder: "Leasehold" },
];
function autoFolder(filename, fallback = "Other") {
  for (const r of FOLDER_RULES) if (r.match.test(filename)) return r.folder;
  return fallback;
}

/* ═══════════════════════════════════════════════════════════════
   1 · UploadSheet
   ═══════════════════════════════════════════════════════════════ */
export function UploadSheet({ open, onClose, dealId, existingDocuments = [], onUploaded }) {
  const [files, setFiles] = useState([]);   // [{file, folder, progress, status, error, duplicate}]
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const existingNames = useMemo(
    () => new Set((existingDocuments || []).map((d) => (d.name || "").toLowerCase()).filter(Boolean)),
    [existingDocuments],
  );

  const reset = useCallback(() => setFiles([]), []);
  const close = useCallback(() => { reset(); onClose?.(); }, [onClose, reset]);

  const addFiles = useCallback((list) => {
    const additions = Array.from(list || []).map((file) => ({
      id: `${file.name}-${file.size}-${file.lastModified || Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      folder: autoFolder(file.name),
      progress: 0,
      status: "queued", // queued | uploading | done | error
      error: null,
      duplicate: existingNames.has(file.name.toLowerCase()),
    }));
    setFiles((prev) => [...prev, ...additions]);
  }, [existingNames]);

  const removeAt = useCallback((id) => setFiles((prev) => prev.filter((f) => f.id !== id)), []);
  const setFolderAt = useCallback((id, folder) => setFiles((prev) => prev.map((f) => f.id === id ? { ...f, folder } : f)), []);

  const uploadAll = useCallback(async () => {
    if (!dealId || files.length === 0) return;
    let okCount = 0;
    for (const entry of files) {
      if (entry.status === "done") continue;
      setFiles((prev) => prev.map((f) => f.id === entry.id ? { ...f, status: "uploading", progress: 0 } : f));
      const fd = new FormData();
      fd.append("file", entry.file, entry.file.name);
      if (entry.folder) fd.append("folder", entry.folder);
      try {
        await api.post(`/api/deals/${dealId}/documents`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 50;
            setFiles((prev) => prev.map((f) => f.id === entry.id ? { ...f, progress: pct } : f));
          },
        });
        setFiles((prev) => prev.map((f) => f.id === entry.id ? { ...f, status: "done", progress: 100 } : f));
        okCount += 1;
      } catch (err) {
        const detail = err?.response?.data?.detail || "Upload failed";
        setFiles((prev) => prev.map((f) => f.id === entry.id ? { ...f, status: "error", error: typeof detail === "string" ? detail : "Upload failed" } : f));
      }
    }
    if (okCount > 0) {
      invalidateDealActivity(dealId);
      onUploaded?.(okCount);
      toast.success(`${okCount} document${okCount === 1 ? "" : "s"} added to this transaction`);
      // Brief delay then close so the user sees the green ticks
      setTimeout(close, 900);
    }
  }, [dealId, files, onUploaded, close]);

  const allDone = files.length > 0 && files.every((f) => f.status === "done");
  const anyUploading = files.some((f) => f.status === "uploading");
  const totalSize = files.reduce((acc, f) => acc + (f.file.size || 0), 0);
  const sizeLabel = totalSize ? `${(totalSize / 1024 / 1024).toFixed(2)} MB` : "—";

  return (
    <SlideOver
      open={open} onClose={close}
      title="Upload documents"
      subtitle="Drop files or pick from device. Auto-categorised by filename."
      testid="upload-sheet"
      footer={(
        <div className="flex items-center gap-2 justify-end">
          <GhostButton onClick={close} testid="upload-cancel">{allDone ? "Done" : "Cancel"}</GhostButton>
          <PrimaryButton
            onClick={uploadAll}
            disabled={files.length === 0 || allDone || anyUploading}
            loading={anyUploading}
            testid="upload-submit"
            icon={UploadIcon}
          >
            {anyUploading ? "Uploading…" : allDone ? "Uploaded" : `Upload ${files.length || ""}`.trim()}
          </PrimaryButton>
        </div>
      )}
    >
      {/* Drop zone */}
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
        className={`w-full rounded-2xl border-2 border-dashed flex flex-col items-center gap-2 py-8 px-6 transition-all ${dragOver ? "border-violet-400 bg-violet-50" : "border-slate-200 bg-slate-50/40 hover:bg-slate-50"}`}
        data-testid="upload-dropzone"
      >
        <span className="w-11 h-11 rounded-xl inline-flex items-center justify-center" style={{ background: "rgba(99,102,241,0.10)", color: "#4338CA" }}>
          <UploadIcon size={17} strokeWidth={2.2} />
        </span>
        <p className="text-[13px] font-semibold text-slate-900">Drag &amp; drop or tap to choose</p>
        <p className="text-[11px] text-slate-500">PDF · DOCX · JPG · PNG · up to 50&nbsp;MB each</p>
      </button>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
        data-testid="upload-input"
      />
      {/* Mobile camera option */}
      <button
        type="button"
        onClick={() => cameraInputRef.current?.click()}
        className="sm:hidden w-full mt-2 h-10 rounded-xl border border-slate-200 bg-white text-[12.5px] font-semibold text-slate-700 inline-flex items-center justify-center gap-1.5"
        data-testid="upload-camera"
      >
        <Paperclip size={12} /> Take a photo or pick from camera
      </button>
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
      />

      {/* Queue */}
      {files.length > 0 && (
        <div className="mt-5 space-y-2" data-testid="upload-queue">
          <div className="flex items-center justify-between px-1">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-slate-500">Files</p>
            <p className="text-[10.5px] text-slate-400 tabular-nums">{files.length} · {sizeLabel}</p>
          </div>
          {files.map((f) => (
            <div
              key={f.id}
              className="rounded-xl bg-white border border-slate-100 px-3 py-2.5 flex items-center gap-3"
              data-testid={`upload-row-${f.id}`}
            >
              <span className="w-9 h-9 rounded-lg inline-flex items-center justify-center shrink-0"
                    style={{ background: "rgba(99,102,241,0.06)", color: "#6366F1" }}>
                <FileText size={13} strokeWidth={2.2} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[12.5px] font-semibold text-slate-900 truncate">{f.file.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <select
                    value={f.folder}
                    onChange={(e) => setFolderAt(f.id, e.target.value)}
                    disabled={f.status !== "queued"}
                    className="text-[10.5px] font-semibold text-slate-500 bg-transparent outline-none disabled:opacity-60"
                    data-testid={`upload-folder-${f.id}`}
                  >
                    {["AML", "Memo", "Contracts", "Mortgage", "Searches", "Leasehold", "Other"].map((k) => (
                      <option key={k} value={k}>{k}</option>
                    ))}
                  </select>
                  <span className="text-[10.5px] text-slate-400">· {(f.file.size / 1024).toFixed(0)} KB</span>
                </div>
                {f.duplicate && f.status === "queued" && (
                  <p className="text-[10.5px] text-amber-700 mt-1 inline-flex items-center gap-1 font-medium">
                    <AlertCircle size={10} /> A file with this name already exists on this transaction.
                  </p>
                )}
                {f.status === "uploading" && (
                  <div className="h-1 mt-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full transition-all"
                         style={{ width: `${f.progress}%`, background: "linear-gradient(90deg,#8B5CF6,#6366F1)" }} />
                  </div>
                )}
                {f.status === "error" && (
                  <p className="text-[10.5px] text-rose-600 mt-1">{f.error}</p>
                )}
              </div>
              {f.status === "done" ? (
                <CheckCircle2 size={15} className="text-emerald-500 shrink-0" strokeWidth={2.4} />
              ) : f.status === "error" ? (
                <AlertCircle size={15} className="text-rose-500 shrink-0" strokeWidth={2.4} />
              ) : f.status === "uploading" ? (
                <Loader2 size={13} className="text-violet-500 animate-spin shrink-0" />
              ) : (
                <button
                  type="button"
                  onClick={() => removeAt(f.id)}
                  className="w-7 h-7 rounded-lg inline-flex items-center justify-center text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors"
                  data-testid={`upload-remove-${f.id}`}
                  aria-label="Remove file"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </SlideOver>
  );
}

/* ═══════════════════════════════════════════════════════════════
   2 · EmailSheet
   ═══════════════════════════════════════════════════════════════ */
const RECIPIENT_GROUPS = [
  { id: "all",        label: "Everyone",         match: () => true },
  { id: "buyer_side", label: "Buyer side",       match: (p) => /buyer/i.test(p.role || p.participant_type || "") },
  { id: "seller_side", label: "Seller side",     match: (p) => /seller/i.test(p.role || p.participant_type || "") },
  { id: "solicitors", label: "Both solicitors",  match: (p) => /solicitor/i.test(p.role || p.participant_type || "") },
];

export function EmailSheet({ open, onClose, dealId, participants = [], onSent, prefill = null }) {
  const [groupId, setGroupId] = useState("all");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // Reply mode — when the sheet is opened with a prefill, we
  // override the recipient group with a direct single-recipient
  // address (typically the participant who just replied) and
  // pre-fill the subject as "Re: …".
  const isReply = !!(prefill && prefill.recipients && prefill.recipients.length);
  useEffect(() => {
    if (!open) return;
    if (prefill?.subject) setSubject(prefill.subject);
    if (prefill?.recipients?.length) setGroupId("__reply");
  }, [open, prefill]);

  const recipients = useMemo(() => {
    if (isReply) return prefill.recipients;
    const rule = RECIPIENT_GROUPS.find((g) => g.id === groupId);
    return emailableRecipients((participants || []).filter(rule?.match || (() => true)));
  }, [participants, groupId, isReply, prefill]);

  const send = useCallback(async () => {
    if (!subject.trim() || !body.trim() || sending) return;
    if (recipients.length === 0) {
      setError("No recipients on this transaction have an email on file.");
      return;
    }
    setSending(true); setError(null);
    const res = await sendOperationalEmail({
      dealId, recipients, subject, body, kind: isReply ? "reply" : "email",
    });
    setSending(false);
    if (res.ok) {
      invalidateDealActivity(dealId);
      toast.success(res.deliveredSummary);
      onSent?.();
      onClose?.();
      setSubject(""); setBody(""); setGroupId("all");
    } else {
      // Preserve draft. Surface inline failure for retry.
      setError(res.errorMessage || "Email delivery failed. Try again.");
    }
  }, [dealId, subject, body, sending, recipients, isReply, onClose, onSent]);

  return (
    <SlideOver
      open={open} onClose={onClose}
      title={isReply ? "Reply" : "Send email"}
      subtitle={isReply
        ? "Replies back through ChainFlow so the conversation lives in the timeline."
        : "Sends from your name with reply-to set to your real work email."}
      testid="email-sheet"
      footer={(
        <div className="flex items-center justify-between gap-3">
          <p className="text-[11px] text-slate-500 tabular-nums">{recipients.length} recipient{recipients.length === 1 ? "" : "s"}</p>
          <div className="flex items-center gap-2">
            <GhostButton onClick={onClose} testid="email-cancel">Cancel</GhostButton>
            <PrimaryButton onClick={send} loading={sending} disabled={!subject.trim() || !body.trim()} testid="email-send" icon={Mail}>{sending ? "Sending…" : (isReply ? "Send reply" : "Send email")}</PrimaryButton>
          </div>
        </div>
      )}
    >
      {isReply ? (
        <>
          <Label>Replying to</Label>
          <ul className="mt-1 space-y-1.5" data-testid="email-reply-recipients">
            {recipients.map((p, i) => (
              <li key={p.user_id || p.id || p.email || i} className="flex items-center gap-2.5 text-[11.5px] text-slate-600">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" aria-hidden />
                <span className="font-semibold text-slate-900">{p.full_name || p.name || "Participant"}</span>
                <span className="text-slate-400">·</span>
                <span className="truncate">{p.email}</span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <Label>Recipient group</Label>
          <div className="flex items-center gap-1.5 flex-wrap" role="tablist">
            {RECIPIENT_GROUPS.map((g) => {
              const active = g.id === groupId;
              return (
                <button
                  key={g.id} type="button" role="tab" aria-selected={active}
                  onClick={() => setGroupId(g.id)}
                  className={`h-8 px-3 rounded-lg text-[11.5px] font-semibold transition-colors ${active ? "text-violet-700 bg-violet-50 border border-violet-200" : "text-slate-600 bg-white border border-slate-200 hover:border-slate-300"}`}
                  data-testid={`email-group-${g.id}`}
                >
                  {g.label}
                </button>
              );
            })}
          </div>
          {recipients.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {recipients.slice(0, 8).map((p, i) => (
                <li key={p.user_id || p.id || i} className="flex items-center gap-2.5 text-[11.5px] text-slate-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-400" aria-hidden />
                  <span className="font-semibold text-slate-900">{p.full_name || p.name || "Participant"}</span>
                  <span className="text-slate-400">·</span>
                  <span className="truncate">{p.email || "(no email on file)"}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {error && <InlineError message={error} onDismiss={() => setError(null)} testid="email-error" />}

      <div className="mt-5">
        <Label htmlFor="email-subject">Subject</Label>
        <input
          id="email-subject" className={inputCls}
          value={subject} onChange={(e) => setSubject(e.target.value)}
          placeholder="Quick update on enquiries"
          data-testid="email-subject"
        />
      </div>
      <div className="mt-4">
        <Label htmlFor="email-body">Message</Label>
        <textarea
          id="email-body" className={textareaCls} rows={8}
          value={body} onChange={(e) => setBody(e.target.value)}
          placeholder="Hi,\n\nA quick operational update from the Deal Room…"
          data-testid="email-body"
        />
      </div>
    </SlideOver>
  );
}

/* ═══════════════════════════════════════════════════════════════
   3 · CallLogSheet
   ═══════════════════════════════════════════════════════════════ */
const CALL_OUTCOMES = [
  { id: "spoke",        label: "Spoke",         tone: "#047857" },
  { id: "left_voicemail", label: "Left voicemail", tone: "#B45309" },
  { id: "no_answer",    label: "No answer",     tone: "#94A3B8" },
  { id: "callback",     label: "Will call back", tone: "#6366F1" },
];

export function CallLogSheet({ open, onClose, dealId, participants = [], onLogged }) {
  const [participantId, setParticipantId] = useState("");
  const [summary, setSummary] = useState("");
  const [outcome, setOutcome] = useState("spoke");
  const [followUp, setFollowUp] = useState(false);
  const [logging, setLogging] = useState(false);

  const participant = participants.find((p) => (p.user_id || p.id) === participantId);

  const log = useCallback(async () => {
    if (!summary.trim() || logging) return;
    setLogging(true);
    const outcomeMeta = CALL_OUTCOMES.find((o) => o.id === outcome);
    const title = `Call · ${participant ? (participant.full_name || participant.name || "participant") : "unspecified"} · ${outcomeMeta?.label || outcome}`;
    try {
      await api.post(`/api/v2/deals/${dealId}/activity`, {
        activity_type: "note",
        title,
        description: summary,
        meta: {
          kind: "call",
          outcome,
          follow_up: followUp,
          participant_id: participantId || null,
          participant_name: participant ? (participant.full_name || participant.name) : null,
          participant_role: participant ? (participant.role || participant.participant_type) : null,
        },
      });
      invalidateDealActivity(dealId);
      toast.success(`Call logged · ${outcomeMeta?.label || outcome}`);
      onLogged?.();
      onClose?.();
      setSummary(""); setOutcome("spoke"); setFollowUp(false); setParticipantId("");
    } catch {
      toast.error("Could not log call");
    } finally {
      setLogging(false);
    }
  }, [dealId, summary, outcome, followUp, participantId, participant, logging, onClose, onLogged]);

  return (
    <SlideOver
      open={open} onClose={onClose}
      title="Log a call"
      subtitle="Captures the outcome on the transaction timeline."
      testid="call-sheet"
      footer={(
        <div className="flex items-center justify-end gap-2">
          <GhostButton onClick={onClose} testid="call-cancel">Cancel</GhostButton>
          <PrimaryButton onClick={log} loading={logging} disabled={!summary.trim()} testid="call-log" icon={Phone}>Log call</PrimaryButton>
        </div>
      )}
    >
      <Label htmlFor="call-participant">Participant</Label>
      <div className="relative">
        <select
          id="call-participant"
          className={`${inputCls} pr-8 appearance-none`}
          value={participantId}
          onChange={(e) => setParticipantId(e.target.value)}
          data-testid="call-participant"
        >
          <option value="">— Select a participant —</option>
          {participants.map((p) => (
            <option key={p.user_id || p.id} value={p.user_id || p.id}>
              {(p.full_name || p.name || "Participant")} · {p.role || p.participant_type || ""}
            </option>
          ))}
        </select>
        <ChevronDown size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      </div>

      <div className="mt-4">
        <Label htmlFor="call-summary">Summary</Label>
        <textarea
          id="call-summary" className={textareaCls} rows={5}
          value={summary} onChange={(e) => setSummary(e.target.value)}
          placeholder="What was discussed? What's next?"
          data-testid="call-summary"
        />
      </div>

      <div className="mt-4">
        <Label>Outcome</Label>
        <div className="flex items-center gap-1.5 flex-wrap" role="radiogroup">
          {CALL_OUTCOMES.map((o) => {
            const active = o.id === outcome;
            return (
              <button
                key={o.id} type="button" role="radio" aria-checked={active}
                onClick={() => setOutcome(o.id)}
                className={`h-8 px-3 rounded-lg text-[11.5px] font-semibold transition-colors border ${active ? "text-white border-transparent" : "text-slate-600 bg-white border-slate-200 hover:border-slate-300"}`}
                style={active ? { background: o.tone } : undefined}
                data-testid={`call-outcome-${o.id}`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>

      <label className="mt-5 flex items-center gap-2.5 cursor-pointer" data-testid="call-followup-wrap">
        <input
          type="checkbox" checked={followUp}
          onChange={(e) => setFollowUp(e.target.checked)}
          className="w-4 h-4 accent-violet-600"
          data-testid="call-followup"
        />
        <span className="text-[12.5px] text-slate-700 font-medium">Follow-up required</span>
      </label>
    </SlideOver>
  );
}

/* ═══════════════════════════════════════════════════════════════
   3.5 · RequestDocSheet — iter-201c calm document request flow
   ═══════════════════════════════════════════════════════════════

   Operational reframe: an agent rarely thinks "I'm logging a
   `document_request` activity". They think "I need the signed
   TA10 from the seller's solicitor". RequestDocSheet front-loads
   that mental model with a calm template chip row + a clear
   recipient picker + optimistic activity insert.

   • Template chips pre-fill title + smart sub-copy.
   • "Request From" picker is derived from the live participants list
     (canonical roles · falls back to the canonical pilot deal's
     seeded participants when the API returns an empty array).
   • On send: optimistic activity is inserted via
     `onRequested(activity)` so the timeline updates instantly,
     even before the server confirms.
   • For pilot deals (`deal._pilot === true`), the request is
     persisted into `chainflow:pilotDocRequests:v1` via the
     chainStore helper so the Documents Hub "Pending Requests"
     lane surfaces it immediately. */
/* iter — Pilot document request templates.
 *
 * Front-loaded for the Stratford / East-London JLL pilot so the
 * operator's first thought ("I need the Mgmt Pack from KFH")
 * resolves to a single tap. Templates retained from earlier
 * iterations (id, proof_of_funds, signed_contract) sit at the
 * end so deep links + saved presets keep working. */
const DOC_TEMPLATES = [
  { id: "memo",                  label: "Memorandum of Sale",   icon: FileText,    defaultRole: "Estate Agent",     title: "Memorandum of Sale requested",      sub: "Issued by the agent confirming the sale particulars" },
  { id: "draft_contract",        label: "Draft Contract",       icon: FileText,    defaultRole: "Seller Solicitor", title: "Draft Contract requested",          sub: "First draft of the sale contract pack" },
  { id: "searches",              label: "Searches",             icon: FileText,    defaultRole: "Buyer Solicitor",  title: "Property searches requested",       sub: "Local authority · drainage · environmental" },
  { id: "mortgage_offer",        label: "Mortgage Offer",       icon: FileText,    defaultRole: "Mortgage Broker",  title: "Mortgage Offer requested",          sub: "Lender's formal mortgage offer" },
  { id: "survey_report",         label: "Survey Report",        icon: FileText,    defaultRole: "Buyer",            title: "Survey Report requested",           sub: "Homebuyer or full structural survey" },
  { id: "management_pack",       label: "Management Pack",      icon: FileText,    defaultRole: "Seller Solicitor", title: "Management Pack requested",         sub: "Leasehold info pack from managing agent" },
  { id: "epc",                   label: "EPC",                  icon: FileText,    defaultRole: "Seller",           title: "EPC requested",                     sub: "Energy Performance Certificate" },
  { id: "ta6",                   label: "TA6",                  icon: FileText,    defaultRole: "Seller Solicitor", title: "TA6 requested",                     sub: "Property Information Form" },
  { id: "ta7",                   label: "TA7",                  icon: FileText,    defaultRole: "Seller Solicitor", title: "TA7 requested",                     sub: "Leasehold Information Form" },
  { id: "ta10",                  label: "TA10",                 icon: FileText,    defaultRole: "Seller Solicitor", title: "TA10 requested",                    sub: "Fittings & Contents Form" },
  { id: "lease",                 label: "Lease / Headlease",    icon: FileText,    defaultRole: "Seller Solicitor", title: "Lease / Headlease requested",       sub: "Copy of the registered lease and any headlease" },
  { id: "service_charge",        label: "Service Charge Accounts", icon: FileText, defaultRole: "Seller Solicitor", title: "Service Charge Accounts requested", sub: "Last 3 years of service charge accounts" },
  { id: "completion_statement",  label: "Completion Statement", icon: FileText,    defaultRole: "Seller Solicitor", title: "Completion Statement requested",    sub: "Final figures statement before completion" },
  { id: "id",                    label: "ID",                   icon: ShieldCheck, defaultRole: "Buyer",            title: "ID requested",                      sub: "Photo ID and proof of address" },
  { id: "proof_of_funds",        label: "Proof of funds",       icon: PoundSterling, defaultRole: "Buyer",          title: "Proof of funds requested",          sub: "Bank statement or solicitor letter" },
  { id: "signed_contract",       label: "Signed contract",      icon: FileText,    defaultRole: "Seller Solicitor", title: "Signed contract requested",         sub: "Final signed contract pack" },
  { id: "other",                 label: "Other",                icon: FileText,    defaultRole: "Seller Solicitor", title: "Document requested",                sub: "Use the note field to specify what you need" },
];

// Template owner label → participant role keys that hold the same responsibility.
const _ROLE_ALIASES = {
  "estate agent": ["agent", "negotiator", "lead agent"],
  "mortgage broker": ["broker"],
};

export function RequestDocSheet({ open, onClose, dealId, participants = [], onRequested, initialTemplateId, docRequests = [], initialRecipientEmail = null }) {
  const [templateId, setTemplateId] = useState(initialTemplateId || "id");
  const [recipientId, setRecipientId] = useState("");
  const [note, setNote] = useState("");
  // iter-P63 · Phase A — Due date is now first-class.  Standard
  // reminders (T-2 / due day / T+1 / T+3) key off this value on
  // the backend, so surfacing it in the sheet is essential.
  // Defaults to +5 business days so operators land on a sensible
  // starting date without an extra tap.
  const [dueAt, setDueAt] = useState("");
  const [sending, setSending] = useState(false);
  const { createPilotDocumentRequest, findDeal } = useChainStore();

  /* iter-alignment · P53 — When the sheet is opened with a
   * contextual template (e.g. from a Current Stage row "Request"
   * button), seed the template picker so the operator lands on
   * the correct row without an extra tap. Runs only on open so
   * a manually-changed template stays sticky. */
  useEffect(() => {
    if (open && initialTemplateId) setTemplateId(initialTemplateId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTemplateId]);

  /* iter-P63 · Default due date to +5 business days from now
   * whenever the sheet opens fresh.  Only writes when empty so a
   * user-supplied value never gets clobbered. */
  useEffect(() => {
    if (!open) return;
    setDueAt((prev) => {
      if (prev) return prev;
      const d = new Date();
      let added = 0;
      while (added < 5) {
        d.setDate(d.getDate() + 1);
        const wd = d.getDay();
        if (wd !== 0 && wd !== 6) added += 1;
      }
      return d.toISOString().slice(0, 10);
    });
  }, [open]);

  const tpl = DOC_TEMPLATES.find((t) => t.id === templateId) || DOC_TEMPLATES[0];
  const recipient = participants.find((p) => (p.user_id || p.id) === recipientId);

  /* iter-P65 · Duplicate-guard.  Prevent creating a second
   * ACTIVE request for the same checklist template while an
   * earlier one is still `requested` / `uploaded` / `rejected`.
   * Reads the SSOT `docRequests` prop the parent room already
   * fetched — no new state, no new fetch. */
  const isDuplicate = useMemo(
    () => hasActiveRequestForTemplate(docRequests, tpl?.id),
    [docRequests, tpl?.id],
  );


  // Preselect only an unambiguous responsible participant: the resolved owner of the open item (by
  // email) first, else the single participant holding the template's role. Otherwise leave it blank.
  useEffect(() => {
    if (!open) return;
    const idOf = (p) => p.user_id || p.id;
    const unique = (list) => (new Set(list.map(idOf)).size === 1 ? list[0] : null);
    const email = (initialRecipientEmail || "").toLowerCase().trim();
    let match = email ? unique(participants.filter((p) => (p.email || "").toLowerCase().trim() === email)) : null;
    const norm = (s) => String(s || "").toLowerCase().replace(/[_\s-]+/g, " ").trim();
    const ownerKey = norm(tpl.defaultRole);
    if (!match && ownerKey) {
      const aliases = new Set([ownerKey, ...(_ROLE_ALIASES[ownerKey] || [])]);
      const roleOf = (p) => norm(p.role || p.role_label || p.participant_type);
      match = unique(participants.filter((p) => aliases.has(roleOf(p))));
    }
    setRecipientId(match ? idOf(match) : "");
  }, [templateId, open, participants, tpl.defaultRole, initialRecipientEmail]);

  const send = useCallback(async () => {
    if (sending) return;
    setSending(true);
    const optimisticAt = new Date().toISOString();
    const recipientName = recipient ? (recipient.full_name || recipient.name) : null;
    const recipientRole = recipient ? (recipient.role || recipient.role_label || recipient.participant_type || tpl.defaultRole) : tpl.defaultRole;
    // Optimistic insert — fire the callback BEFORE the round-trip.
    onRequested?.({
      id: `optimistic-${Date.now()}`,
      activity_type: "document_request",
      title: tpl.title,
      description: recipient
        ? `Requested from ${recipientName}${note ? ` · ${note.slice(0, 60)}` : ""}`
        : (note || tpl.sub),
      at: optimisticAt,
      created_at: optimisticAt,
    });
    // iter-270I — Pilot deals are frontend-only by design. When the
    // deal is a pilot deal (or the API isn't available), the pilot
    // helper writes the request locally so the Documents Hub
    // "Pending Requests" lane + Deal Room activity feed update
    // immediately. For real backend deals we additionally fire the
    // activity API call so the canonical timeline persists.
    const dealRecord = typeof findDeal === "function" ? findDeal(dealId) : null;
    const isPilot = !!(dealRecord && dealRecord._pilot);
    if (isPilot) {
      try {
        createPilotDocumentRequest?.(dealId, {
          template_id: tpl.id,
          title: tpl.title,
          doc_label: tpl.label,
          requested_from_role: recipientRole || null,
          requested_from_name: recipientName,
          requested_from_id:   recipientId || null,
          note: note || null,
        });
      } catch (_e) { /* fail soft — optimistic activity already fired */ }
    }
    try {
      if (!isPilot) {
        await api.post(`/api/v2/deals/${dealId}/activity`, {
          activity_type: "document_request",
          title: tpl.title,
          description: recipient
            ? `Requested from ${recipientName}`
            : tpl.sub,
          meta: {
            template_id: tpl.id,
            recipient_id: recipientId || null,
            recipient_name: recipientName,
            due_at: dueAt || null,
            note: note || null,
          },
        });
        // Also persist the structured request so it appears in the
        // canonical Pending Requests lane.
        // iter-P63 · Phase A — the POST also triggers outbound
        // email dispatch + reminder cadence scheduling on the
        // backend when `recipient_participant_id` + `due_at` are
        // present.
        try {
          await api.post(`/api/deals/${dealId}/document-requests`, {
            doc_type: tpl.id,
            label: tpl.label,
            requested_from: recipientRole || null,
            recipient_participant_id: recipientId || null,
            due_at: dueAt || null,
            note: note || null,
          });
        } catch (_e) { /* doc-requests endpoint optional; activity already logged */ }
      }
      invalidateDealActivity(dealId);
      onClose?.();
      setNote("");
      setDueAt("");
    } catch {
      if (!isPilot) toast.error("Could not send document request");
    } finally {
      setSending(false);
    }
  }, [dealId, sending, tpl, recipientId, recipient, note, dueAt, onRequested, onClose, createPilotDocumentRequest, findDeal]);

  return (
    <SlideOver
      open={open} onClose={onClose}
      title="Request a document"
      subtitle="Pick a template, choose the recipient, send. The timeline updates instantly."
      testid="request-doc-sheet"
      footer={(
        <div className="flex items-center justify-end gap-2">
          <GhostButton onClick={onClose} testid="request-doc-cancel">Cancel</GhostButton>
          <PrimaryButton onClick={send} loading={sending} disabled={sending || isDuplicate || (participants.length > 0 && !recipient)} testid="request-doc-send" icon={Send}>
            Send request
          </PrimaryButton>
        </div>
      )}
    >
      {isDuplicate && (
        <div
          className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200/70 bg-amber-50/60 px-3 py-2 text-[11.5px] text-amber-800"
          data-testid="request-doc-duplicate-warning"
          role="status"
        >
          <span className="mt-0.5 shrink-0">⚠</span>
          <div>
            <p className="font-semibold text-amber-900">Already requested</p>
            <p className="mt-0.5 text-amber-800/90 leading-snug">
              An active <span className="font-semibold">{tpl?.label || "document"}</span> request already exists for this transaction. Chase or verify the existing one in the Document Requests workspace below.
            </p>
          </div>
        </div>
      )}
      <Label>Document</Label>
      <div className="flex items-center gap-1.5 flex-wrap" role="radiogroup" data-testid="request-doc-templates">
        {DOC_TEMPLATES.map((t) => {
          const active = t.id === templateId;
          const Icon = t.icon || FileText;
          return (
            <button
              key={t.id} type="button" role="radio" aria-checked={active}
              onClick={() => setTemplateId(t.id)}
              className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[11.5px] font-semibold transition-colors ${
                active
                  ? "text-white"
                  : "text-slate-700 bg-white hover:bg-slate-50"
              }`}
              style={{
                background: active ? "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)" : undefined,
                boxShadow: active ? undefined : "0 0 0 1px rgba(226,232,240,0.9)",
              }}
              data-testid={`request-doc-template-${t.id}`}
            >
              <Icon size={11} strokeWidth={2.2} />
              {t.label}
            </button>
          );
        })}
      </div>
      <p className="text-[11.5px] text-slate-500 mt-2 leading-snug">{tpl.sub}</p>

      <div className="mt-4">
        <Label htmlFor="request-doc-recipient">Request From</Label>
        <div className="relative">
          <select
            id="request-doc-recipient"
            className={`${inputCls} pr-8 appearance-none`}
            value={recipientId}
            onChange={(e) => setRecipientId(e.target.value)}
            data-testid="request-doc-recipient"
          >
            <option value="">— Pick a participant —</option>
            {participants.map((p) => (
              <option key={p.user_id || p.id} value={p.user_id || p.id}>
                {(p.full_name || p.name || "Participant")} · {p.role || p.role_label || p.participant_type || ""}
              </option>
            ))}
          </select>
          <ChevronDown size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>
        {recipient && (
          <p className="text-[10.5px] text-slate-500 mt-1.5 inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
            Will be sent to {recipient.full_name || recipient.name}.
          </p>
        )}
        {!recipient && participants.length > 0 && (
          <p className="text-[10.5px] text-amber-700 mt-1.5" data-testid="request-doc-recipient-required">
            Choose who to request this from.
          </p>
        )}
        {!recipient && participants.length === 0 && (
          <p className="text-[10.5px] text-slate-500 mt-1.5">
            No participants on this deal yet — add one from the Participants rail.
          </p>
        )}
      </div>

      <div className="mt-4">
        <Label htmlFor="request-doc-due">Due date</Label>
        <input
          id="request-doc-due"
          type="date"
          className={inputCls}
          value={dueAt}
          onChange={(e) => setDueAt(e.target.value)}
          min={new Date().toISOString().slice(0, 10)}
          data-testid="request-doc-due"
        />
        <p className="text-[10.5px] text-slate-500 mt-1.5 leading-snug">
          Reminders auto-schedule at T-2 days, on the due date, T+1 and T+3 until fulfilled.
        </p>
      </div>

      <div className="mt-4">
        <Label htmlFor="request-doc-note">Note <span className="text-slate-400 font-normal">(optional)</span></Label>
        <textarea
          id="request-doc-note"
          className={inputCls + " min-h-[68px] resize-none"}
          placeholder="Add a brief note for the recipient (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="request-doc-note"
        />
      </div>
    </SlideOver>
  );
}

/* ═══════════════════════════════════════════════════════════════
   4 · TaskSheet
   ═══════════════════════════════════════════════════════════════ */
const PRIORITIES = [
  { id: "low",    label: "Low",    tone: "#94A3B8" },
  { id: "normal", label: "Normal", tone: "#6366F1" },
  { id: "high",   label: "High",   tone: "#F59E0B" },
  { id: "urgent", label: "Urgent", tone: "#EF4444" },
];

export function TaskSheet({ open, onClose, dealId, participants = [], suggestion = null, onCreated }) {
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [priority, setPriority] = useState("normal");
  const [creating, setCreating] = useState(false);

  // iter-201c — smart contextual defaults. When the caller passes
  // a `suggestion` object (typically derived from the deal's top
  // blocker), pre-fill the title + assignee + priority so the
  // operator only has to confirm. Reads as operational shortcut,
  // never as auto-fill noise.
  useEffect(() => {
    if (!open) return;
    if (suggestion?.title && !title)              setTitle(suggestion.title);
    if (suggestion?.assignee_id && !assigneeId)   setAssigneeId(suggestion.assignee_id);
    if (suggestion?.priority)                     setPriority(suggestion.priority);
    if (suggestion?.due && !due)                  setDue(suggestion.due);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, suggestion?.title, suggestion?.assignee_id, suggestion?.priority, suggestion?.due]);

  const assignee = participants.find((p) => (p.user_id || p.id) === assigneeId);
  const overdue = useMemo(() => {
    if (!due) return false;
    return Date.parse(due) < Date.now() - 60_000;
  }, [due]);

  const create = useCallback(async () => {
    if (!title.trim() || creating) return;
    setCreating(true);
    const prTone = PRIORITIES.find((p) => p.id === priority);
    const optimisticAt = new Date().toISOString();
    // iter-201c — optimistic insert. Surface the task in the
    // timeline BEFORE the API confirms so the room feels alive.
    onCreated?.({
      id: `optimistic-${Date.now()}`,
      activity_type: "task_created",
      title: title.slice(0, 120),
      description: due
        ? `Due ${new Date(due).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`
        : "No due date",
      at: optimisticAt,
      created_at: optimisticAt,
      meta: {
        priority,
        assignee_id: assigneeId || null,
        assignee_name: assignee ? (assignee.full_name || assignee.name) : null,
        due_at: due || null,
        overdue,
      },
    });
    try {
      await api.post(`/api/v2/deals/${dealId}/activity`, {
        activity_type: "task_created",
        title: title.slice(0, 120),
        description: due ? `Due ${new Date(due).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : "No due date",
        meta: {
          priority,
          assignee_id: assigneeId || null,
          assignee_name: assignee ? (assignee.full_name || assignee.name) : null,
          due_at: due || null,
          overdue,
        },
      });
      // Risk escalation — only when overdue at creation OR urgent.
      if (overdue || priority === "urgent") {
        await api.post(`/api/v2/deals/${dealId}/activity`, {
          activity_type: "risk_flagged",
          title: `Task ${overdue ? "overdue" : "urgent"} · ${title.slice(0, 80)}`,
          description: assignee ? `Assigned to ${assignee.full_name || assignee.name}` : "Unassigned",
          meta: { kind: "task", priority, due_at: due || null },
        }).catch(() => {});
      }
      invalidateDealActivity(dealId);
      onClose?.();
      setTitle(""); setDue(""); setAssigneeId(""); setPriority("normal");
    } catch {
      // Calm inline fallback — keep the sheet open with the draft.
      toast.error(`Could not create ${prTone?.label || priority} task`);
    } finally {
      setCreating(false);
    }
  }, [dealId, title, due, assigneeId, assignee, priority, overdue, creating, onClose, onCreated]);

  return (
    <SlideOver
      open={open} onClose={onClose}
      title="Create task"
      subtitle="Lands on Today, surfaces in the timeline, escalates if overdue."
      testid="task-sheet"
      footer={(
        <div className="flex items-center justify-end gap-2">
          <GhostButton onClick={onClose} testid="task-cancel">Cancel</GhostButton>
          <PrimaryButton onClick={create} loading={creating} disabled={!title.trim()} testid="task-create" icon={ListChecks}>Create task</PrimaryButton>
        </div>
      )}
    >
      <Label htmlFor="task-title">Task</Label>
      <input
        id="task-title" className={inputCls}
        value={title} onChange={(e) => setTitle(e.target.value)}
        placeholder="Chase Vale Legal on enquiries response"
        data-testid="task-title"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        <div>
          <Label htmlFor="task-due">Due</Label>
          <input
            id="task-due" type="date"
            className={inputCls}
            value={due} onChange={(e) => setDue(e.target.value)}
            data-testid="task-due"
          />
          {overdue && (
            <p className="text-[10.5px] text-rose-600 font-semibold mt-1.5 inline-flex items-center gap-1">
              <AlertCircle size={11} /> Overdue — will surface as a risk.
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="task-assignee">Assign to</Label>
          <div className="relative">
            <select
              id="task-assignee"
              className={`${inputCls} pr-8 appearance-none`}
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              data-testid="task-assignee"
            >
              <option value="">— Unassigned —</option>
              {participants.map((p) => (
                <option key={p.user_id || p.id} value={p.user_id || p.id}>
                  {(p.full_name || p.name || "Participant")} · {p.role || p.participant_type || ""}
                </option>
              ))}
            </select>
            <ChevronDown size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>
      </div>

      <div className="mt-4">
        <Label>Priority</Label>
        <div className="flex items-center gap-1.5 flex-wrap" role="radiogroup">
          {PRIORITIES.map((p) => {
            const active = p.id === priority;
            return (
              <button
                key={p.id} type="button" role="radio" aria-checked={active}
                onClick={() => setPriority(p.id)}
                className={`h-8 px-3 rounded-lg text-[11.5px] font-semibold transition-colors border ${active ? "text-white border-transparent" : "text-slate-600 bg-white border-slate-200 hover:border-slate-300"}`}
                style={active ? { background: p.tone } : undefined}
                data-testid={`task-priority-${p.id}`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>
    </SlideOver>
  );
}

/* ═══════════════════════════════════════════════════════════════
   5 · MessageAllSheet  (broadcast composer)
   ═══════════════════════════════════════════════════════════════ */
const MESSAGE_TEMPLATES = [
  {
    id: "weekly_update",
    label: "Weekly update",
    subject: "Weekly transaction update",
    body: "Hi everyone,\n\nA quick operational update on where we are this week:\n\n• \n• \n• \n\nHappy to jump on a call if anything needs discussion.\n\nThanks!",
  },
  {
    id: "exchange_target",
    label: "Exchange target confirmation",
    subject: "Confirming exchange target",
    body: "Hi all,\n\nAhead of exchange, please confirm:\n\n• Funds in place\n• Final enquiries cleared\n• Signed contract returned\n\nWe're targeting exchange this week.\n\nThanks!",
  },
  {
    id: "blocker_check",
    label: "Blocker check-in",
    subject: "Quick blocker check-in",
    body: "Hi,\n\nA quick operational check to make sure nothing is sitting unactioned. Please let me know if anything is blocking your side so we can keep the chain moving.\n\nThanks!",
  },
];

export function MessageAllSheet({ open, onClose, dealId, participants = [], onSent }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // Pre-select everyone with a usable email when opened
  useEffect(() => {
    if (open) {
      const ids = emailableRecipients(participants).map((p) => p.user_id || p.id).filter(Boolean);
      setSelectedIds(new Set(ids));
      setError(null);
    }
  }, [open, participants]);

  const toggle = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectAll = () => setSelectedIds(new Set(emailableRecipients(participants).map((p) => p.user_id || p.id).filter(Boolean)));
  const selectNone = () => setSelectedIds(new Set());

  const applyTemplate = (tpl) => { setSubject(tpl.subject); setBody(tpl.body); };

  const recipients = useMemo(
    () => emailableRecipients(participants).filter((p) => selectedIds.has(p.user_id || p.id)),
    [participants, selectedIds],
  );

  const send = useCallback(async () => {
    if (!subject.trim() || !body.trim() || sending || recipients.length === 0) return;
    setSending(true); setError(null);
    const res = await sendOperationalEmail({
      dealId, recipients, subject, body, kind: "broadcast",
    });
    setSending(false);
    if (res.ok) {
      invalidateDealActivity(dealId);
      toast.success(res.deliveredSummary);
      onSent?.();
      onClose?.();
      setSubject(""); setBody("");
    } else {
      setError(res.errorMessage || "Broadcast delivery failed. Try again.");
    }
  }, [dealId, subject, body, recipients, sending, onSent, onClose]);

  return (
    <SlideOver
      open={open} onClose={onClose}
      title="Message all participants"
      subtitle="One calm broadcast. Logged once on the transaction timeline."
      wide
      testid="message-all-sheet"
      footer={(
        <div className="flex items-center justify-between gap-3">
          <p className="text-[11px] text-slate-500 tabular-nums">{recipients.length} of {participants.length} selected</p>
          <div className="flex items-center gap-2">
            <GhostButton onClick={onClose} testid="message-all-cancel">Cancel</GhostButton>
            <PrimaryButton
              onClick={send}
              disabled={!subject.trim() || !body.trim() || recipients.length === 0}
              loading={sending}
              testid="message-all-send"
              icon={Send}
            >
              Send to {recipients.length}
            </PrimaryButton>
          </div>
        </div>
      )}
    >
      {/* Recipients */}
      <div className="flex items-center justify-between">
        <Label>Recipients</Label>
        <div className="flex items-center gap-2 -mt-1.5">
          <button type="button" onClick={selectAll}  className="text-[11px] font-semibold text-violet-700 hover:underline" data-testid="message-all-select-all">All</button>
          <span className="text-slate-300">·</span>
          <button type="button" onClick={selectNone} className="text-[11px] font-semibold text-slate-500 hover:underline" data-testid="message-all-select-none">None</button>
        </div>
      </div>
      <ul className="space-y-1.5">
        {participants.length === 0 && (
          <li className="text-[12px] text-slate-500 px-1 py-2">No participants on this transaction yet.</li>
        )}
        {participants.map((p, i) => {
          const id = p.user_id || p.id;
          const checked = selectedIds.has(id);
          return (
            <li key={id || i}>
              <button
                type="button"
                onClick={() => toggle(id)}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-colors text-left ${checked ? "bg-violet-50" : "hover:bg-slate-50"}`}
                data-testid={`message-all-recipient-${i}`}
              >
                <span className={`w-4 h-4 rounded inline-flex items-center justify-center shrink-0 ${checked ? "" : "border border-slate-300"}`}
                      style={checked ? { background: "linear-gradient(135deg,#8B5CF6,#6366F1)" } : undefined}>
                  {checked && <Check size={10} strokeWidth={3} className="text-white" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[12.5px] font-semibold text-slate-900 truncate">
                    {p.full_name || p.name || "Participant"}
                  </span>
                  <span className="block text-[10.5px] text-slate-500 truncate">
                    {p.role || p.participant_type || ""}{p.email ? <> · {p.email}</> : null}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {/* Templates */}
      <div className="mt-5">
        <Label>Templates</Label>
        <div className="flex items-center gap-1.5 flex-wrap">
          {MESSAGE_TEMPLATES.map((tpl) => (
            <button
              key={tpl.id} type="button" onClick={() => applyTemplate(tpl)}
              className="h-8 px-3 rounded-lg text-[11.5px] font-semibold text-slate-600 bg-white border border-slate-200 hover:border-violet-300 hover:text-violet-700 transition-colors"
              data-testid={`message-all-template-${tpl.id}`}
            >
              {tpl.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <Label htmlFor="message-all-subject">Subject</Label>
        <input
          id="message-all-subject" className={inputCls}
          value={subject} onChange={(e) => setSubject(e.target.value)}
          placeholder="Weekly transaction update"
          data-testid="message-all-subject"
        />
      </div>
      <div className="mt-4">
        <Label htmlFor="message-all-body">Message</Label>
        <textarea
          id="message-all-body" className={textareaCls} rows={8}
          value={body} onChange={(e) => setBody(e.target.value)}
          placeholder="Hi everyone,\n\nA quick operational update…"
          data-testid="message-all-body"
        />
      </div>

      {error && <InlineError message={error} onDismiss={() => setError(null)} testid="message-all-error" />}
    </SlideOver>
  );
}
